<!-- eslint-disable no-prototype-builtins -->
<script setup lang="ts">
import AppState, {
  calculateMaxAmount,
  contactLabelPluralLower,
  contactLabelSingular,
  contactLabelSingularLower,
  getFormsInLocation,
  recurringAppointmentPaymentOptions,
  setEditCalendarValidationData,
} from '@/states/app'
import {
  DotsVerticalIcon,
  InfoCircleIcon,
  Percent02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UITooltip as GHLUITooltip,
  UIAlert,
  UICheckbox,
  UIDivider,
  UIForm,
  UIFormItem,
  UIHeader,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UIRadio,
  UISelect,
  UISwitch,
  UITabPane,
  UITabs,
} from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch, watchEffect } from 'vue'
import { VueDraggableNext } from 'vue-draggable-next'
import { useI18n } from 'vue-i18n'
import {
  DepositType,
  GuestType,
  RecurringAppointmentsPaymentType,
  WidgetConfig,
  WidgetType,
} from '../../models/calendar'
import { currency } from '../../utils/currencyHelper'
import {
  handleCustomValueModification,
  thankyouMessageFieldOptions,
  thankyouMessageFieldOptionsV3,
  thankyouMessageFieldRecurringOptions,
} from '../../utils/customValueMapper'
import {
  checkForValidPaymentProvider,
  getTooltip,
} from '../../utils/generalFunctions'
import {
  getDefaulPaymentMethod,
  getSupportedPaymentProviders,
} from '../../utils/paymentHelper'
import DependentSettings from '../common/DependentSettings.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
import AddGuestModal from './AddGuestModal.vue'

const defaultPageOrder = [
  {
    position: 0,
    kind: 'calendar',
  },
  {
    position: 1,
    kind: 'form',
  },
]

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  shouldAcceptPayment: false,
  reorderedPageOption: [] as { position: number; kind: string }[] | undefined,
  shouldAcceptDepositAmount: false,
  isClassicWidget: false,
  formData: {
    stickyContact: false,
    autoConfirm: true,
    formId: '',
    pixelId: '',
    formSubmitType: 'ThankYouMessage',
    formSubmitRedirectURL: '',
    formSubmitThanksMessage: '',
    isLivePaymentMode: false,
    stripe: {
      amount: 0,
      currency: 'USD',
      chargeDescription: '',
      depositAmount: 0,
      depositPercentage: 0,
      depositType: DepositType.AMOUNT,
      recurringAppointmentsPaymentType:
        RecurringAppointmentsPaymentType.FirstAppointmentOnly,
      isCouponEnabled: false,
    },
    enableGuests: false,
    enableChargeGuests: false,
    enableMandatoryGuestType: false,
    guestType: GuestType.COLLECT_DETAIL,
    shouldSendCustomForm: false,
    enableConsentCheck: false,
    consentLabel: '' as string | undefined,
    widgetConfig: undefined as WidgetConfig | undefined,
  },
  isFormFirstEnabled: false,
  isPaymentError: false,
  showDependentSettings: false,
})
const listLabels = {
  calendar: t('calendar_advanced.forms_payment.date_and_time_selector'),
  form: t('calendar_advanced.forms_payment.form_selector'),
}
const stripeFormRef = ref()

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const isClassBookingCalendar = computed(() => {
  return editingCalendar.value?.isClassBookingCalendar
})

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const enableAddGuests = computed(() => {
  return !isClassBookingCalendar.value && !isServiceCalendar.value
})

const disableFormOrder = computed(() => {
  return AppState.isClassicWidgetSelected
})

const paymentCurrencyOptions = computed(() => {
  const currencyOptions: string[] = []
  for (const key of Object.keys(currency)) {
    currencyOptions.push(currency[key].code)
  }
  return currencyOptions.map(x => {
    return {
      value: x,
      label: x,
    }
  })
})

const formOptions = computed(() => {
  const customFormOptions = AppState.forms.map(x => {
    return {
      value: x._id,
      label: x.name,
    }
  })

  return [
    {
      value: '',
      label: t('calendar_advanced.forms_payment.default_form'),
    },
    ...customFormOptions,
  ]
})

const isRecurringPaymentEnabled = computed(() => {
  return AppState.editCalendarValidationData.enableRecurring
})

const isPaypalWithRazorpay = computed(() => {
  return AppState.defaultPaymentProvider === 'custom-provider' && AppState.isPayPalEnabled
})

watch(
  () => formOptions.value,
  () => {
    if (formOptions.value.length === 0) {
      return
    }

    const formOption = formOptions.value.find(
      x => x.value === state.formData.formId
    )

    if (!formOption) {
      state.formData.formId = ''
    }
  }
)

watch(
  () => state.formData.enableGuests,
  () => {
    setEditCalendarValidationData('enableGuests', state.formData.enableGuests)
  },
  { deep: true }
)

watch(
  () => state.formData.enableMandatoryGuestType,
  () => {
    setEditCalendarValidationData(
      'enableMandatoryGuestType',
      state.formData.enableMandatoryGuestType
    )
  },
  { deep: true }
)

watch(
  () => state.isFormFirstEnabled,
  () => {
    AppState.isFormFirstEnabled = state.isFormFirstEnabled
  }
)

watch(
  () => AppState.editCalendarValidationData.disableFormFirst,
  newValue => {
    if (newValue) {
      state.isFormFirstEnabled = false
      AppState.isFormFirstEnabled = false
      state.formData.widgetConfig.pageOrder = [
        { position: 0, kind: 'calendar' },
        { position: 1, kind: 'form' },
      ]
      state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
    }
  }
)

watch(
  () => AppState.isFormFirstEnabled,
  newValue => {
    if (!newValue) {
      return
    }

    const { widgetConfig } = state.formData || {}
    if (widgetConfig?.pageOrder) {
      widgetConfig.pageOrder.sort((a, b) => (a.kind === 'form' ? -1 : 1))
    } else {
      state.formData.widgetConfig.pageOrder = [
        { position: 1, kind: 'calendar' },
        { position: 0, kind: 'form' },
      ]
    }
  }
)

const isPartialPaymentEnabled = computed(() => {
  return (
    state.formData.stripe.amount > 0 &&
    !AppState.editCalendarValidationData.enableRecurring
  )
})

const rules = computed(() => {
  return {
    stripeAmount: {
      trigger: ['input', 'blur'],
      validator() {
        if (!state.formData.stripe.amount) {
          state.isPaymentError = false
          return true
        }
        if (state.formData.stripe.amount.toString().match(/^\d+(\.\d{3,})$/)) {
          state.isPaymentError = true
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        state.isPaymentError = false
        return true
      },
    },
    depositAmount: {
      trigger: ['input', 'blur'],
      validator() {
        if (
          state.formData.stripe.depositAmount > state.formData.stripe.amount
        ) {
          return new Error(
            t('calendar_advanced.forms_payment.deposit_amount_error')
          )
        }
        if (
          state.formData.stripe.depositAmount
            .toString()
            .match(/^\d+(\.\d{3,})$/)
        ) {
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        return true
      },
    },
    depositPercentage: {
      trigger: ['input', 'blur'],
      validator() {
        if (state.formData.stripe.depositPercentage > 100) {
          return new Error(
            t('calendar_advanced.forms_payment.deposit_percentage_error')
          )
        }

        if (
          state.formData.stripe.depositPercentage
            .toString()
            .match(/^\d+(\.\d{3,})$/)
        ) {
          return new Error(t('calendar_advanced.forms_payment.stripe_error'))
        }
        return true
      },
    },
    chargeDescription: {
      trigger: ['input', 'blur'],
      validator() {
        if (!state.formData.stripe.chargeDescription) {
          return true
        }
        if (state.formData.stripe.chargeDescription.length > 1000) {
          return new Error(
            t('calendar_advanced.forms_payment.payment_description_error')
          )
        }
        return true
      },
    },
  }
})

const emit = defineEmits(['onSave', 'onCancel'])

const onClickTab = tab => {
  state.formData.stripe.depositType = tab
}

const thankyouMessageRef = ref()
const thankyouMessageRecurringRef = ref()

watchEffect(() => {
  const customValues = handleCustomValueModification(AppState.customValues)

  const thankyouOptions = editingCalendar.value?.isEventCalendar
    ? thankyouMessageFieldOptions
    : thankyouMessageFieldOptionsV3
  thankyouMessageRef.value = customValues
    ? [...thankyouOptions, customValues]
    : thankyouOptions

  thankyouMessageRecurringRef.value = customValues
    ? [...thankyouMessageFieldRecurringOptions, customValues]
    : thankyouMessageFieldRecurringOptions
})

const methods = {
  async loadData() {
    state.shouldAcceptPayment = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('amount')
    )
    state.shouldAcceptDepositAmount = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('deposit')
    )
    state.formData = {
      stickyContact: Boolean(editingCalendar.value?.stickyContact),
      isLivePaymentMode: Boolean(editingCalendar.value?.isLivePaymentMode),
      autoConfirm: Boolean(editingCalendar.value?.autoConfirm),
      formId: editingCalendar.value?.formId || '',
      pixelId: editingCalendar.value?.pixelId || '',
      formSubmitType:
        editingCalendar.value?.formSubmitType || 'ThankYouMessage',
      formSubmitRedirectURL: editingCalendar.value?.formSubmitRedirectURL || '',
      formSubmitThanksMessage:
        editingCalendar.value?.formSubmitThanksMessage || '',
      stripe: {
        amount: String(editingCalendar.value?.stripe?.amount || 0) || '0',
        currency: editingCalendar.value?.stripe?.currency || 'USD',
        chargeDescription:
          editingCalendar.value?.stripe?.chargeDescription || '',
        depositAmount:
          (editingCalendar.value?.stripe?.depositType === DepositType.AMOUNT &&
            editingCalendar.value?.stripe?.deposit) ||
          0,
        depositPercentage:
          (editingCalendar.value?.stripe?.depositType ===
            DepositType.PERCENTAGE &&
            editingCalendar.value?.stripe?.deposit) ||
          0,
        depositType:
          editingCalendar.value?.stripe?.depositType || DepositType.AMOUNT,
        recurringAppointmentsPaymentType:
          editingCalendar.value?.stripe?.recurringAppointmentsPaymentType ||
          RecurringAppointmentsPaymentType.FirstAppointmentOnly,
        isCouponEnabled:
          editingCalendar.value?.stripe?.isCouponEnabled || false,
      },
      enableGuests: Boolean(editingCalendar.value?.enableGuests),
      enableMandatoryGuestType: Boolean(
        editingCalendar.value?.enableMandatoryGuestType
      ),
      guestType: editingCalendar.value?.guestType || GuestType.COLLECT_DETAIL,
      enableChargeGuests: Boolean(editingCalendar.value?.enableChargeGuests),
      shouldSendCustomForm: Boolean(
        editingCalendar.value?.shouldSendCustomForm
      ),
      enableConsentCheck: Boolean(editingCalendar.value?.enableConsentCheck),
      consentLabel: editingCalendar.value?.consentLabel,
      widgetConfig: editingCalendar.value?.widgetConfig || {},
    }
    if (state.formData.widgetConfig) {
      state.formData.widgetConfig.pageOrder =
        state.formData?.widgetConfig?.pageOrder || defaultPageOrder
    }
    state.isClassicWidget =
      WidgetType.CLASSIC === editingCalendar.value?.widgetType
    state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
    const pageOrder = state.reorderedPageOption?.find(
      (x: any) => x.kind === 'form'
    )
    state.isFormFirstEnabled = pageOrder?.position === 0
    await Promise.all([getFormsInLocation()])
  },
  async emitSave() {
    const payload = {
      ...state.formData,
    } as any

    if (state.shouldAcceptPayment) {
      try {
        await stripeFormRef.value.getForm().validate()
      } catch (error) {
        return
      }
      payload.stripe = {
        amount: state.formData.stripe.amount,
        currency: state.formData.stripe.currency,
        chargeDescription: state.formData.stripe.chargeDescription,
        isCouponEnabled: state.formData.stripe.isCouponEnabled,
      }

      if (state.shouldAcceptDepositAmount) {
        payload.stripe = {
          ...payload.stripe,
          deposit:
            state.formData.stripe.depositType === DepositType.AMOUNT
              ? state.formData.stripe.depositAmount
              : state.formData.stripe.depositPercentage,
          depositType: state.formData.stripe.depositType,
        }
      }

      if (isRecurringPaymentEnabled.value) {
        payload.stripe = {
          ...payload.stripe,
          recurringAppointmentsPaymentType:
            state.formData.stripe.recurringAppointmentsPaymentType,
        }
      }
    } else {
      payload.stripe = {}
      payload.enableChargeGuests = false
    }
    if (payload?.stripe?.amount) {
      payload.stripe.amount = Number(payload.stripe.amount)
    }
    const reorderedPageOption = state.reorderedPageOption?.map(
      (item, index) => ({
        kind: item?.kind,
        position: index,
      })
    )
    if (reorderedPageOption?.length) {
      payload.widgetConfig.pageOrder = reorderedPageOption
    }

    payload.enableChargeGuests = state.shouldAcceptPayment
      ? state.formData.enableChargeGuests
      : false

    return payload
  },
  handleOptionChange(e) {
    const pageOrder = state.reorderedPageOption?.at(0)
    state.isFormFirstEnabled = pageOrder?.kind === 'form'
    AppState.isFormFirstEnabled = pageOrder?.kind === 'form'
    if (
      !state.isFormFirstEnabled &&
      AppState.editCalendarValidationData.enableSameUserAssignment
    ) {
      state.showDependentSettings = true
    }
  },
}

defineExpose({
  methods,
})

watch(
  () => state.shouldAcceptPayment,
  newValue => {
    AppState.shouldAcceptPayment = newValue
    if (!newValue) {
      state.formData.enableChargeGuests = false
    }
  }
)

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)

watch(
  () => state.formData.stripe.amount,
  newValue => {
    if (newValue <= 0) {
      state.shouldAcceptDepositAmount = false
      state.formData.stripe.deposit = 0
    }
  }
)

watch(
  () => state.shouldAcceptDepositAmount,
  () => (AppState.isPartialPaymentChecked = state.shouldAcceptDepositAmount)
)

// Define a ref to control the modal visibility
const showModal = ref(false)

// Method to handle the UISwitch toggle
const handleEnableGuestToggle = value => {
  state.formData.enableGuests = value
  if (value && state.shouldAcceptPayment) {
    showModal.value = true
  }
  if (!value) {
    state.formData.enableChargeGuests = false
    state.formData.enableMandatoryGuestType = false
  }
}

const handleGuestModalConfirm = (enableChargeGuests: boolean) => {
  state.formData.enableChargeGuests = enableChargeGuests
}

watch(
  () => AppState.isPartialPaymentChecked,
  () => {
    if (!AppState.isPartialPaymentChecked) {
      state.shouldAcceptDepositAmount = false
    }
  }
)

watch(
  () => AppState.editCalendarValidationData.recurringPaymentType,
  () => {
    state.formData.stripe.recurringAppointmentsPaymentType =
      AppState.editCalendarValidationData.recurringPaymentType
  }
)

const isConnectedPaymentProviderSupported = computed(() => {
  return checkForValidPaymentProvider(AppState.paymentProviderInfo)
})

const isPaymentProviderConnected = computed(() => {
  return !!AppState.defaultPaymentProvider || AppState.isPayPalEnabled
})

const statusClasses = computed(() => {
  if (!isPaymentProviderConnected.value) {
    return 'bg-error-100 text-error-700'
  }
  return isConnectedPaymentProviderSupported.value
    ? 'bg-primary-50 text-primary-700'
    : 'bg-warning-50 text-warning-700'
})

const paymentProviderMessage = computed(() => {
  return isConnectedPaymentProviderSupported.value || (AppState.isPayPalEnabled && AppState.defaultPaymentProvider === 'paypal')
    ? t('calendar_advanced.forms_payment.default')
    : isPaymentProviderConnected.value
    ? t('calendar_advanced.forms_payment.provider_not_supported')
    : t('calendar_advanced.forms_payment.no_default_payment_provider')
})

const onClickConnectPaymentProvider = () => {
  AppState.parentApp?.then(parent => {
    parent.emit('go-to-payments-integrations-page', {
      providerId: undefined,
    })
  })
}

const insertThanksMessage = (value: string) => {
  state.formData.formSubmitThanksMessage =
    state.formData.formSubmitThanksMessage + ' ' + value
}

const cancelDependentSettings = () => {
  state.showDependentSettings = false
  state.formData.widgetConfig.pageOrder = [
    { position: 0, kind: 'form' },
    { position: 1, kind: 'calendar' },
  ]
  state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
}

const confirmDependentSettings = () => {
  state.showDependentSettings = false
  state.isFormFirstEnabled = false
  AppState.editCalendarValidationData.enableSameUserAssignment = false
}

const maxAmount = computed(() => {
  return calculateMaxAmount(state.formData.stripe.currency)
})
</script>

<template>
  <UIHeader
    id="calendars-forms-payments-tab"
    :secondary-title="$t('calendar_advanced.forms_payment.forms')"
    :description="t('calendar_advanced.forms_payment.forms_description')"
  />
  <UIDivider />

  <UIFormItem
    :label="$t('calendar_advanced.forms_payment.select_form')"
    path="formData.formId"
    :tooltip="
      getTooltip($t('calendar_advanced.forms_payment.select_form_tooltip'))
    "
  >
    <div class="mt-1 flex flex-col">
      <UISelect
        id="calendar-selectForm"
        v-model:value="state.formData.formId"
        class="w-96"
        :filterable="true"
        :options="formOptions"
      />
    </div>
  </UIFormItem>
  <UIFormItem
    :label="t('calendar_advanced.forms_payment.widget_order_label')"
    path="formData.formId"
    :tooltip="
      getTooltip(t('calendar_advanced.forms_payment.widget_order_tool_tip'))
    "
  >
    <div
      v-if="state.formData.widgetConfig?.pageOrder"
      class="flex w-full flex-row"
    >
      <div class="w-[12%] px-4">
        <div
          v-for="(_, index) in state.formData.widgetConfig?.pageOrder"
          :key="'step-' + index"
          class="flex w-full cursor-move items-center space-x-5 py-4"
        >
          <div class="w-full font-medium text-gray-700">
            {{
              `${t('calendar_advanced.forms_payment.order_step')} ${index + 1} `
            }}
          </div>
        </div>
      </div>
      <VueDraggableNext
        :list="state.reorderedPageOption"
        class="dragArea list-group w-[36%] rounded-md border"
        :disabled="disableFormOrder"
        @change="methods.handleOptionChange"
      >
        <transition-group name=" flip-list">
          <div
            v-for="(pageOrder, index) in state.formData.widgetConfig?.pageOrder"
            :key="pageOrder?.kind + index"
            :class="disableFormOrder ? 'cursor-no-drop' : 'cursor-move'"
          >
            <UITooltip
              v-if="disableFormOrder"
              :title="
                !AppState.isClassicWidgetSelected
                  ? t(
                      'calendar_advanced.forms_payment.widget_re_order_disabled_tooltip',
                      {
                        contactLabel: contactLabelSingularLower,
                      }
                    )
                  : t('calendar_advanced.forms_payment.widget_re_order_tooltip')
              "
              placement="top"
            >
              <div
                class="flex cursor-move items-center space-x-5 border py-4 font-medium"
                :class="disableFormOrder ? 'cursor-no-drop' : 'cursor-move'"
              >
                <div class="pl-2">
                  <DotsVerticalIcon class="h-5 w-4 text-gray-500" />
                  <DotsVerticalIcon class="-ml-2 h-5 w-4 text-gray-500" />
                </div>
                <div class="capitalize text-gray-700">
                  {{ listLabels[pageOrder.kind] }}
                </div>
              </div>
            </UITooltip>
            <div
              v-else
              class="flex cursor-move items-center space-x-5 border py-4 font-medium"
              :class="disableFormOrder ? 'cursor-no-drop' : 'cursor-move'"
            >
              <div class="pl-2">
                <DotsVerticalIcon class="h-5 w-4 text-gray-500" />
                <DotsVerticalIcon class="-ml-2 h-5 w-4 text-gray-500" />
              </div>
              <div class="capitalize text-gray-700">
                {{ listLabels[pageOrder.kind] }}
              </div>
            </div>
          </div>
        </transition-group>
      </VueDraggableNext>
    </div>
  </UIFormItem>
  <div
    v-if="state.formData.formId === ''"
    class="flex items-center space-x-3 pb-3"
  >
    <div id="Sticky-Contacts-Toggle">
      <UISwitch
        v-model:value="state.formData.stickyContact"
        :aria-label="$t('aria_labels.sticky_contacts')"
        size="small"
      />
    </div>
    <div class="flex flex-row items-center space-x-3">
      <div class="font-normal">
        {{ $t('calendar_advanced.forms_payment.sticky_contacts') }}
      </div>
      <UITooltip
        :title="
          $t('calendar_advanced.forms_payment.sticky_contacts_tooltip', {
            contactsLabel: contactLabelPluralLower,
          })
        "
        placement="top"
      >
        <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
      </UITooltip>
    </div>
  </div>

  <div v-if="state.formData.formId === ''" class="">
    <div class="flex space-x-3 pb-3">
      <div id="consent-form-toggle">
        <UISwitch
          v-model:value="state.formData.enableConsentCheck"
          size="small"
          :aria-label="$t('aria_labels.consent_form')"
        />
      </div>
      <div class="font-normal">
        {{ $t('calendar_advanced.forms_payment.consent_label') }}
      </div>
      <UITooltip
        :title="
          $t('calendar_advanced.forms_payment.consent_tooltip', {
            contactsLabel: contactLabelPluralLower,
          })
        "
        placement="top"
      >
        <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
      </UITooltip>
    </div>
    <UIInput
      v-if="state.formData.enableConsentCheck"
      id="consent-form-input"
      v-model:value="state.formData.consentLabel"
      type="textarea"
      :rows="4"
      class="mb-3 !w-96 items-center"
      :placeholder="
        $t('calendar_advanced.forms_payment.consent_input_placeholder')
      "
    />
  </div>

  <div v-if="enableAddGuests" class="flex items-center space-x-3 pb-3">
    <div id="enable-guest-toggle">
      <UISwitch
        v-model:value="state.formData.enableGuests"
        size="small"
        :aria-label="$t('aria_labels.enable_guests')"
        @update:value="value => handleEnableGuestToggle(value)"
      />
    </div>
    <div class="font-normal">
      {{ $t('calendar_advanced.forms_payment.enable_guests') }}
    </div>
    <UITooltip
      :title="$t('calendar_advanced.forms_payment.add_guest_tooltip')"
      placement="top"
    >
      <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
    </UITooltip>
  </div>
  <div v-if="state.formData.enableGuests" class="mt-2 flex">
    <UIRadio
      id="calendar-guest-type-collect-detail"
      class="p-1"
      :checked="state.formData.guestType === GuestType.COLLECT_DETAIL"
      name="custom"
      @change="state.formData.guestType = GuestType.COLLECT_DETAIL"
    >
      {{ $t('calendar_advanced.forms_payment.add_guest_collect_email') }}
    </UIRadio>
    <UIRadio
      id="calendar-guest-type-count-only"
      class="p-1"
      :checked="state.formData.guestType === GuestType.COUNT_ONLY"
      name="default"
      @change="state.formData.guestType = GuestType.COUNT_ONLY"
    >
      {{ $t('calendar_advanced.forms_payment.add_guest_count_only') }}
    </UIRadio>
  </div>
  <div
    v-if="
      state.formData.enableGuests &&
      state.formData.formId !== '' &&
      state.formData.guestType === GuestType.COLLECT_DETAIL
    "
    class="ml-1 mt-4 flex flex-row items-center"
  >
    <UICheckbox
      id="calendar-guest-custom-form-notification"
      v-model:checked="state.formData.shouldSendCustomForm"
    >
      {{ $t('calendar_advanced.forms_payment.custom_email_notification') }}
    </UICheckbox>
    <UITooltip
      :title="
        $t('calendar_advanced.forms_payment.add_guest_custom_form_tooltip')
      "
      placement="top"
      class=""
    >
      <InfoCircleIcon class="w-4 text-gray-500" />
    </UITooltip>
  </div>

  <div v-if="state.formData.enableGuests" class="mt-4">
    <UICheckbox
      id="calendar-guest-mandatory-checkbox"
      v-model:checked="state.formData.enableMandatoryGuestType"
      class="ml-1 flex flex-row items-center"
    >
      {{ $t('calendar_advanced.forms_payment.require_guest_for_booking') }}
    </UICheckbox>

    <div class="mb-2 ml-1 mt-1 text-gray-500">
      {{ $t('calendar_advanced.forms_payment.enable_mandatory_guest') }}
    </div>
  </div>

  <UIDivider />

  <UIFormItem
    :label="$t('calendar_advanced.forms_payment.confirmation_page')"
    path="isDefaultConfirmationPage"
    :tooltip="getTooltip($t('calendar_advanced.forms_payment.confirm_tooltip'))"
  >
    <div class="flex w-full flex-col space-y-4">
      <div
        class="leading-20 w-full pl-0.5 text-left text-sm font-normal text-gray-500"
      >
        {{ $t('calendar_advanced.forms_payment.forms_sub') }}
      </div>
      <div class="flex space-x-4">
        <UIRadio
          id="calendar-default-thankyou-message"
          :checked="state.formData.formSubmitType === 'ThankYouMessage'"
          name="default"
          @change="state.formData.formSubmitType = 'ThankYouMessage'"
        >
          {{ $t('calendar_advanced.forms_payment.default') }}
        </UIRadio>

        <UIRadio
          id="calendar-redirect-url"
          :checked="state.formData.formSubmitType === 'RedirectURL'"
          name="custom"
          @change="state.formData.formSubmitType = 'RedirectURL'"
        >
          {{ $t('calendar_advanced.forms_payment.redirect_url') }}
        </UIRadio>
      </div>

      <div
        v-if="state.formData.formSubmitType === 'ThankYouMessage'"
        class="w-96"
      >
        <UIFormItem
          :label="$t('calendar_advanced.forms_payment.thank_you')"
          path="formData.formSubmitThanksMessage"
        >
          <UIInput
            id="redirect-url-input"
            v-model:value="state.formData.formSubmitThanksMessage"
            type="textarea"
            :rows="4"
            class="relative"
          >
            <template #suffix>
              <CustomVariablePickerV2
                :dropdown-options="
                  AppState.editCalendarValidationData.enableRecurring ||
                  AppState.isFormFirstEnabled
                    ? thankyouMessageRecurringRef
                    : thankyouMessageRef
                "
                :button-class="'absolute bottom-0 right-4 cursor-pointer'"
                @update:selected-value="val => insertThanksMessage(val)"
              />
            </template>
          </UIInput>
        </UIFormItem>
      </div>

      <div v-if="state.formData.formSubmitType === 'RedirectURL'" class="w-96">
        <UIInput
          id="redirect-url-input"
          v-model:value="state.formData.formSubmitRedirectURL"
          :placeholder="$t('calendar_advanced.forms_payment.enter_url')"
        />
      </div>
    </div>
  </UIFormItem>

  <UIFormItem
    :label="$t('calendar_advanced.forms_payment.pixel_id')"
    path="formData.pixelId"
    :tooltip="
      getTooltip($t('calendar_advanced.forms_payment.pixel_id_tooltip'))
    "
  >
    <div class="w-96">
      <UIInput
        id="calendar-pixel-id"
        v-model="state.formData.pixelId"
        :placeholder="$t('placeholders.pixel_id')"
      />
    </div>
  </UIFormItem>

  <UIDivider />

  <div class="flex items-center space-x-3 pb-3">
    <div id="calendar-autoConfirm-switch">
      <UISwitch
        v-model:value="state.formData.autoConfirm"
        :aria-label="$t('aria_labels.auto_confirm')"
        size="small"
      />
    </div>
    <div class="font-normal">
      {{
        isServiceCalendar
          ? $t('calendar_advanced.forms_payment.auto_confirm_service')
          : $t('calendar_advanced.forms_payment.auto_confirm')
      }}
    </div>
    <UITooltip
      :title="$t('calendar_advanced.forms_payment.auto_confirm_tooltip')"
      placement="top"
    >
      <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
    </UITooltip>
  </div>

  <div class="">
    <UIDivider />

    <UIHeader
      id="text-only-header"
      :secondary-title="$t('labels.payment')"
      :description="$t('calendar_advanced.forms_payment.configure_payment')"
    />

    <UIDivider />

    <div>
      <div class="flex w-full flex-row items-center justify-between">
        <div class="flex items-center space-x-3 pb-3">
          <div id="calendars-acceptPayments-switch">
            <UISwitch
              v-model:value="state.shouldAcceptPayment"
              :aria-label="$t('aria_labels.should_accept_payment')"
              size="small"
              :disabled="!isPaymentProviderConnected"
            />
          </div>

          <div class="font-normal">
            {{ $t('calendar_advanced.forms_payment.accept_payments') }}
          </div>
          <UITooltip
            v-if="!isServiceCalendar"
            :title="
              isPaymentProviderConnected
                ? $t('calendar_advanced.forms_payment.accept_payments_tooltip')
                : $t(
                    'calendar_advanced.forms_payment.payment_disable_text_integration'
                  )
            "
            placement="top"
          >
            <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
          </UITooltip>
        </div>
      </div>
    </div>

    <div class="">
      <UIForm
        id="stripe-payment-form"
        ref="stripeFormRef"
        :model="state.formData"
        :rules="rules"
      >
        <UIFormItem
          v-if="state.shouldAcceptPayment"
          :label="$t('create_calendar.total_amount')"
          class="mt-4 w-full"
          path="stripeAmount"
          :show-feedback="state.isPaymentError"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.forms_payment.total_amount_tooltip')
            )
          "
        >
          <UIInputGroup>
            <UIInputNumber
              id="calendars-stripe-amount"
              v-model="state.formData.stripe.amount"
              placeholder=""
              :min="0"
              :max="maxAmount"
              :show-button="false"
              size="large"
              class="w-full"
            />
            <UISelect
              id="calendars-stripe-currency"
              v-model:value="state.formData.stripe.currency"
              :filterable="true"
              class="w-32"
              :options="paymentCurrencyOptions"
            />
          </UIInputGroup>
        </UIFormItem>

        <div
          class="mt-[10px] flex flex-row items-center justify-between"
          :class="{
            'mb-[20px]': isRecurringPaymentEnabled,
            'pb-2': isPaypalWithRazorpay,
          }"
        >
          <div class="flex flex-row items-center !gap-2">
            <div
              v-if="isPaymentProviderConnected"
              class="min-w-[30px] max-w-[90px]"
            >
              <img
                :title="AppState.defaultPaymentProvider"
                class="object-contain"
                :src="getDefaulPaymentMethod(AppState.defaultPaymentProvider)"
                :alt="$t('alt_text.payment_provider')"
              />
            </div>
            <div
              class="w-fit rounded-2xl px-2 py-0.5 text-[13px] font-normal"
              :class="statusClasses"
            >
              {{ paymentProviderMessage }}
            </div>
          </div>

          <div class="flex cursor-pointer flex-row items-center gap-2">
            <div
              class="cursor-pointer text-[13px] font-medium text-primary-700"
              role="button"
              @click="onClickConnectPaymentProvider"
              @keydown="onClickConnectPaymentProvider"
            >
              {{
                $t('calendar_advanced.forms_payment.manage_payment_provider')
              }}
            </div>
            <GHLUITooltip>
              <template #trigger>
                <InfoCircleIcon class="h-4 w-4 text-gray-500" />
              </template>
              <div :style="{ fontFamily: 'Inter' }">
                {{ t('calendar_advanced.forms_payment.supported_providers') }}:
                <br />
                <li
                  v-for="provider in getSupportedPaymentProviders()"
                  :key="provider"
                  class="list-none"
                >
                  {{ provider }}
                </li>
              </div>
            </GHLUITooltip>
          </div>
        </div>
        <div v-if="isPaypalWithRazorpay" class="mt-1" :class="{ 'mb-5': isRecurringPaymentEnabled }" >
          <UIAlert id="notification-alert" :closable="false" type="warning" class="text-sm bg-white">
          <template #content>{{ t('calendar_advanced.forms_payment.paypal_razorpay_helper_text') }}</template>
        </UIAlert>
        </div>

        <div
          v-if="
            state.shouldAcceptPayment &&
            !AppState.editCalendarValidationData.enableRecurring
          "
          class="mb-5 mt-5"
        >
          <div
            v-if="isPartialPaymentEnabled"
            class="mt-2 flex items-center space-x-3"
          >
            <UISwitch
              id="calendars-collect-deposit"
              v-model:value="state.shouldAcceptDepositAmount"
              size="small"
              :aria-label="$t('aria_labels.collect_deposit')"
            />
            <div class="font-normal">
              {{ $t('create_calendar.collect_deposit_amount') }}
            </div>
            <UITooltip
              :title="
                $t('calendar_advanced.forms_payment.partial_payment_tooltip')
              "
              placement="top"
            >
              <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
            </UITooltip>
          </div>

          <div
            v-else-if="state.formData.stripe.amount <= 0"
            class="flex items-center space-x-3"
          >
            <UITooltip
              placement="left"
              :title="
                $t(
                  'calendar_advanced.forms_payment.partial_payment_enable_error'
                )
              "
            >
              <div class="flex items-center space-x-3">
                <UISwitch
                  id="calendars-collect-deposit"
                  v-model:value="state.shouldAcceptDepositAmount"
                  :disabled="true"
                  size="small"
                  :aria-label="$t('aria_labels.collect_deposit')"
                />

                <div
                  class="font-normal"
                  :class="{ 'opacity-50': state.formData.stripe.amount <= 0 }"
                >
                  {{ $t('create_calendar.collect_deposit_amount') }}
                </div>
              </div>
            </UITooltip>
            <UITooltip
              :title="
                $t('calendar_advanced.forms_payment.partial_payment_tooltip')
              "
              placement="top"
            >
              <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
            </UITooltip>
          </div>

          <div
            v-if="state.shouldAcceptDepositAmount && isPartialPaymentEnabled"
            class="relative mt-4 flex w-52"
          >
            <UITabs
              v-model="state.formData.stripe.depositType"
              :default-value="state.formData.stripe.depositType"
              :type="'segment'"
              :animated="true"
              @update:value="val => onClickTab(val)"
            >
              <UITabPane name="amount" :tab="$t('create_calendar.amount')">
                <div class="hidden">
                  {{ $t('calendar_advanced.forms_payment.amount_tab_hidden_label') }}
                </div>
              </UITabPane>
              <UITabPane
                name="percentage"
                :tab="$t('create_calendar.percentage')"
              >
                <div class="hidden">
                  {{
                    $t(
                      'calendar_advanced.forms_payment.percentage_tab_hidden_label'
                    )
                  }}
                </div>
              </UITabPane>
            </UITabs>
          </div>

          <div
            v-if="state.shouldAcceptDepositAmount && isPartialPaymentEnabled"
            class="mt-2 flex w-80 flex-col"
          >
            <UIFormItem
              :path="
                state.formData.stripe.depositType === DepositType.AMOUNT
                  ? 'depositAmount'
                  : 'depositPercentage'
              "
              :label="$t('create_calendar.deposit_amount')"
              :tooltip="
                getTooltip(
                  $t('calendar_advanced.forms_payment.deposit_amount_tooltip')
                )
              "
            >
              <UIInputGroup
                v-if="state.formData.stripe.depositType === DepositType.AMOUNT"
              >
                <UIInputNumber
                  id="calendars-deposit-stripe-amount"
                  v-model="state.formData.stripe.depositAmount"
                  placeholder=""
                  :min="0"
                  :max="maxAmount"
                  :show-button="false"
                  size="large"
                  class="w-full"
                />
                <UIInputGroupLabel class="w-20 text-center">{{
                  state.formData.stripe.currency
                }}</UIInputGroupLabel>
              </UIInputGroup>

              <UIInputGroup v-else>
                <UIInputNumber
                  id="calendars-deposit-stripe-amount"
                  v-model="state.formData.stripe.depositPercentage"
                  placeholder=""
                  :min="0"
                  :max="maxAmount"
                  :show-button="false"
                  size="large"
                  class="w-full"
                />
                <UIInputGroupLabel class="w-20 text-center">
                  <Percent02Icon class="mb-1 h-5 w-5" />
                </UIInputGroupLabel>
              </UIInputGroup>
            </UIFormItem>
          </div>
        </div>

        <div
          v-if="state.formData.enableGuests && state.shouldAcceptPayment"
          class="mb-5 flex items-center space-x-3"
          :class="{ '-mt-6': state.shouldAcceptDepositAmount }"
        >
          <UISwitch
            v-model:value="state.formData.enableChargeGuests"
            size="small"
            :aria-label="$t('aria_labels.enable_charge_guests')"
          />
          <div class="font-normal">
            {{
              $t(
                'calendar_advanced.forms_payment.multiply_amount_guests_all_guests'
              )
            }}
          </div>

          <UITooltip
            :title="
              $t(
                'calendar_advanced.forms_payment.multiply_amount_guests_all_guests_tooltip'
              )
            "
          >
            <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
          </UITooltip>
        </div>

        <div
          v-if="state.shouldAcceptPayment && AppState.isCouponEnabled"
          class="mb-5 flex items-center space-x-3"
        >
          <UISwitch
            v-model:value="state.formData.stripe.isCouponEnabled"
            size="small"
            :aria-label="$t('aria_labels.enable_coupon')"
          />
          <div class="font-normal">
            {{ $t('calendar_advanced.forms_payment.enable_coupon') }}
          </div>
          <UITooltip
            :title="$t('calendar_advanced.forms_payment.enable_coupon_tooltip')"
          >
            <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
          </UITooltip>
        </div>

        <UIFormItem
          v-if="state.shouldAcceptPayment && isRecurringPaymentEnabled"
          :label="$t('calendar_advanced.forms_payment.recurring_payment_label')"
          path="chargeDescription"
        >
          <UISelect
            id="calendar-select-recurring-appointment-payment"
            v-model:value="
              state.formData.stripe.recurringAppointmentsPaymentType
            "
            class="w-96"
            :options="recurringAppointmentPaymentOptions(t)"
          />
        </UIFormItem>

        <UIFormItem
          v-if="state.shouldAcceptPayment"
          :label="$t('calendar_advanced.forms_payment.payment_description')"
          path="chargeDescription"
        >
          <UIInput
            id="calendar-stripe-currency-input"
            v-model="state.formData.stripe.chargeDescription"
            type="textarea"
            :placeholder="
              $t('calendar_advanced.forms_payment.payment_description_tooltip')
            "
            :rows="4"
            :show-count="true"
            :maxlength="1000"
          />
        </UIFormItem>
      </UIForm>

      <div
        v-if="state.shouldAcceptPayment"
        class="mt-3 flex flex-col items-start pb-2"
      >
        <div class="flex flex-row items-center gap-3 pb-2">
          <div class="font-medium text-gray-700">
            {{ $t('calendar_advanced.forms_payment.payment_mode') }}
          </div>
          <UITooltip
            :title="$t('calendar_advanced.forms_payment.payment_mode_tooltip')"
            placement="top"
          >
            <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
          </UITooltip>
          <div class="font-medium text-gray-700">:</div>
          <div class="font-normal">
            {{ $t('calendar_advanced.forms_payment.test') }}
          </div>
          <div id="calendars-payment-live">
            <UISwitch
              v-model:value="state.formData.isLivePaymentMode"
              :aria-label="$t('aria_labels.payment_live')"
              size="small"
            />
          </div>
          <div class="font-normal">
            {{ $t('calendar_advanced.forms_payment.live') }}
          </div>
        </div>
      </div>
      <div
        v-if="state.shouldAcceptPayment && !state.formData.isLivePaymentMode"
        class="flex items-center space-x-3 pb-3"
      >
        <UIAlert id="test-mode-warning-alert" type="warning" :closable="false">
          <template #content>
            <div
              class="whitespace-pre-line"
              v-html="$t('calendar_advanced.forms_payment.test_mode_warning')"
            />
          </template>
        </UIAlert>
      </div>
    </div>

    <DependentSettings
      v-if="state.showDependentSettings"
      :show-modal="state.showDependentSettings"
      :title="t('calendar_advanced.forms_payment.change_form_order')"
      :desc="
        t('calendar_advanced.forms_payment.change_form_order_desc', {
          contactLabel: contactLabelSingular,
        })
      "
      @cancel-click="cancelDependentSettings"
      @confirm-click="confirmDependentSettings"
    />
  </div>

  <AddGuestModal
    v-model:show="showModal"
    v-model:chargeGuestsEnabled="state.formData.enableChargeGuests"
    @onConfirm="handleGuestModalConfirm"
  />
</template>
